import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import { usePageMeta } from '../hooks/usePageMeta';
import { showInfo, showSuccess, showError } from '../store/slices/toastSlice';
import { authApi } from '../services/authApi';
import { userApi } from '../services/userApi';

const Profile: React.FC = () => {
  usePageMeta('Profile – AskNow', 'Manage your profile information and account details.');

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);

  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
  });

  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setForm({
        username: user.username || '',
        email: (user as any).email || '',
        first_name: (user as any).first_name || '',
        last_name: (user as any).last_name || '',
      });
    }
  }, [user]);

  const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      // Call API to update user
      await userApi.updateUser(user.id, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email
      });

      dispatch(showSuccess({ title: 'Saved', message: 'Profile updated successfully.', duration: 3000 }));
    } catch (error: any) {
      dispatch(showError({ title: 'Update Failed', message: error.message, duration: 4000 }));
    }
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialog({
      open: true,
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog(prev => ({ ...prev, open: false }));
  };

  const handleSubmitPassword = async () => {
    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      dispatch(showError({ title: 'Error', message: 'New passwords do not match', duration: 3000 }));
      return;
    }
    if (passwordDialog.newPassword.length < 8) {
      dispatch(showError({ title: 'Error', message: 'Password must be at least 8 characters', duration: 3000 }));
      return;
    }

    try {
      await authApi.changePassword(passwordDialog.oldPassword, passwordDialog.newPassword);
      dispatch(showSuccess({ title: 'Success', message: 'Password changed successfully', duration: 3000 }));
      handleClosePasswordDialog();
    } catch (error: any) {
      dispatch(showError({ title: 'Failed', message: error.message, duration: 4000 }));
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3} gap={2}>
        <Avatar sx={{ width: 56, height: 56 }}>{(user?.username || 'U').charAt(0).toUpperCase()}</Avatar>
        <Box>
          <Typography variant="h4">Your Profile</Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage your personal information
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Username" value={form.username} onChange={handleChange('username')} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" value={form.email} onChange={handleChange('email')} fullWidth type="email" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="First name" value={form.first_name} onChange={handleChange('first_name')} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last name" value={form.last_name} onChange={handleChange('last_name')} fullWidth />
            </Grid>
          </Grid>

          <Box mt={3} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSave}>Save Changes</Button>
            <Button variant="outlined" onClick={handleOpenPasswordDialog}>Change Password</Button>
          </Box>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog.open} onClose={handleClosePasswordDialog}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              value={passwordDialog.oldPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, oldPassword: e.target.value })}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={passwordDialog.newPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, newPassword: e.target.value })}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              value={passwordDialog.confirmPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, confirmPassword: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button onClick={handleSubmitPassword} variant="contained">Update Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;


